/**
 * Catalog of downloadable, pre-formatted data-collection templates offered on the
 * Templates page. This is the single source of truth shared by the UI (renders the
 * list), the desktop IPC handler, and the web route (both resolve an id → file to
 * stream). React never derives file paths from user input — handlers look an id up
 * here, so an unknown id can never reach the filesystem.
 *
 * `filename` is the canonical name on disk inside the bundled `data-templates/`
 * directory (repo root in dev; `process.resourcesPath/data-templates` on desktop;
 * `TEMPLATES_DIR` in the web container). `downloadFilename` is the human-facing name
 * the user receives — it carries the attachment's original wording for versioning.
 */
export type TemplateCategory = 'Basic' | 'Advanced' | 'Finance' | 'GCC'

export interface TemplateCatalogEntry {
  /** Stable, URL-safe identifier used by the download API boundary. */
  id: string
  category: TemplateCategory
  /** Display name shown on the Templates page. */
  name: string
  /** One-line summary of what the template collects. */
  description: string
  /** Canonical on-disk filename inside the `data-templates/` directory. */
  filename: string
  /** Filename the user receives (Content-Disposition / Save dialog default). */
  downloadFilename: string
}

export const TEMPLATE_CATALOG: readonly TemplateCatalogEntry[] = [
  {
    id: 'acn-data-input-basic',
    category: 'Basic',
    name: 'ACN – Talent Navigator Data Input Template',
    description:
      'Headcount structure, function mapping, and department inputs — the standard employee-roster collection file to hand a client at kickoff.',
    filename: 'acn-talent-navigator-data-input-template.xlsx',
    downloadFilename: 'ACN - Talent Navigator Data Input Template 9.xlsx',
  },
  {
    id: 'talent-reinvention-advanced',
    category: 'Advanced',
    name: 'Talent Reinvention Data Request (Advanced)',
    description:
      'Expanded HR data collection — compensation, regional variances, and contingent workers — for deeper workforce-reinvention engagements.',
    filename: 'talent-reinvention-data-request-advanced.xlsx',
    downloadFilename: 'Talent Reinvention Data Request Template (Advanced) - Client.xlsx',
  },
]

/** Order categories appear in on the Templates page. */
export const TEMPLATE_CATEGORY_ORDER: readonly TemplateCategory[] = [
  'Basic',
  'Advanced',
  'Finance',
  'GCC',
]

/** Resolve a catalog entry by id. Returns undefined for unknown ids. */
export function getTemplateById(id: string): TemplateCatalogEntry | undefined {
  return TEMPLATE_CATALOG.find((t) => t.id === id)
}
